import * as React from 'react';
import { GdsService_OrderAdditionalServiceGdsServiceVip_Fragment } from '@websky/graphql';
import { useTranslation } from 'react-i18next';
import { Money as MoneyType } from '../../../types';
import { useTheme } from '../../../theme';
import DescriptionBlock from '../DescriptionBlock/DescriptionBlock';
import SwitchBlock from '../SwitchBlock/SwitchBlock';
import Money from '../../../Money';
import { WxEvents } from '../../../DataLayerAnalytics/types';
import { useEffect, useMemo } from 'react';
import WebskyAnalyticsApollo from '../../../DataLayerAnalytics/WebskyAnalyticsApollo';
import { ConfirmOptionVariant } from '../../types';
import cn from 'classnames';
import { useConfig } from '../../../context';
import { SafeHtml } from '@shared/ui';

export interface Props {
	freezeUntil?: number;
	addedToOrder?: boolean;
	price?: MoneyType;
	onChange?: React.Dispatch<boolean>;
	confirmed?: boolean;
	variant: ConfirmOptionVariant;
	service?: GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;
	isActive?: boolean;
	isDisabled?: boolean;
	isOld?: boolean;
	isLoading?: boolean;
}

const ConfirmOption: React.FC<Props> = ({
	onChange,
	price,
	addedToOrder,
	confirmed,
	freezeUntil,
	variant,
	service,
	isActive,
	isDisabled,
	isOld: isOldFromProps,
	isLoading
}) => {
	const { FareLock: css } = useTheme('FareLock');
	const { t } = useTranslation('FareLock');

	const { isOldConfirmOption } = useConfig().global;
	const isOld = isOldFromProps ?? isOldConfirmOption;

	const getTitle = useMemo(() => {
		let title = '';

		switch (variant) {
			case 'FareLock':
				if (addedToOrder) {
					title = t('Check your selection and pay fare lock');
				} else if (confirmed) {
					title = t('Confirmed fare lock');
				} else {
					title = t('Fixation the cost of the air ticket');
				}
				break;

			case 'Refund':
				title = service?.name?.length ? service.name : t('Ticket Refund Option Available');
				break;

			case 'Upgrade':
				title = t('UpgradeFlightClass:Get upgraded now!');
				break;

			case 'Note':
				title = t('Please note:', { nsSeparator: '|' });
				break;
		}

		return (
			<>
				{title}
				{confirmed && price && (
					<>
						: <Money money={price} />
					</>
				)}
			</>
		);
	}, [variant, addedToOrder, confirmed, service?.name, price]);

	const getDescription = useMemo(() => {
		let description = '';
		switch (variant) {
			case 'FareLock':
				description = addedToOrder
					? t(
							'If you make the decision to fly on the selected date within the next {{hours}} {{plural}}, you will be able to pay the full amount of the booking at the fare you locked in. Please, be advised that Fare lock fee is non-refundable.',
							{
								hours: freezeUntil,
								plural: t('hours', { count: freezeUntil })
							}
					  )
					: t(
							'No need to worry about the price increase or about the fact that all tickets will be sold out, now you can fix the ticket price for {{hours}} {{plural}}',
							{
								hours: freezeUntil,
								plural: t('hours', { count: freezeUntil })
							}
					  );
				break;

			case 'Refund':
				description =
					service?.description ||
					t(
						'You can get a full refund for your ticket if you have more than 24 hours left before departure. Add the service now — it will be unavailable later.'
					);
				break;

			case 'Upgrade':
				description = service?.description;
				break;

			case 'Note':
				description = t(
					'to manage bookings made on the SCAT International website, it is necessary to use this <a href="https://scat.international.tickets.kz/orders/avia" target="_blank">link</a>',
					{ nsSeparator: '|' }
				);
				break;

			default:
				break;
		}

		return <SafeHtml tag="div" html={description} />;
	}, [variant, service?.description, freezeUntil, addedToOrder]);

	useEffect(() => {
		if (!addedToOrder) {
			WebskyAnalyticsApollo.dispatchWxViewEvent(WxEvents.PriceFreeze);
		}
	}, [price?.amount, confirmed]);

	return (
		<div
			className={cn(css.root, {
				[css.root_old]: isOld,
				[css.root_disabled]: isDisabled,
				[css.root_refund]: variant === 'Refund',
				[css.root_fareLock]: variant === 'FareLock',
				[css.root_upgrade]: variant === 'Upgrade',
				[css.root_note]: variant === 'Note'
			})}
		>
			<DescriptionBlock
				variant={variant}
				showConfirmedIcon={confirmed}
				title={getTitle}
				description={getDescription}
				isOld={isOld}
				isDisabled={isDisabled}
			/>

			{!addedToOrder && !['Upgrade', 'Note'].includes(variant) && (
				<SwitchBlock
					price={price}
					isActive={isActive}
					isDisabled={isDisabled}
					onChange={onChange}
					variant={variant}
					isOld={isOld}
					isLoading={isLoading}
				/>
			)}
		</div>
	);
};

export default ConfirmOption;
